import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import usePjoRejection from '../hooks/usePjoRejection';
import { useCurrencyDisplay } from '../../hooks/useCurrencyDisplay';
import { getUserProfile } from '../../store/slices/authSlice';

const formatDateTime = (datetime) =>
  new Date(datetime).toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const useCountdown = (deadline) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!deadline) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [deadline]);

  return useMemo(() => {
    if (!deadline) return { expired: false, label: '' };

    const diff = new Date(deadline).getTime() - now;
    if (diff <= 0) {
      return { expired: true, label: 'Deadline passed' };
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (hours > 0) {
      return {
        expired: false,
        label: `${hours}h ${minutes}m ${seconds}s`,
      };
    }
    return {
      expired: false,
      label: `${minutes}m ${seconds}s`,
      urgent: minutes < 30,
    };
  }, [deadline, now]);
};

const OPTION_ICONS = {
  full_refund: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  ),
  change_flight: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
    </svg>
  ),
  rejected_leg_refund: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
    </svg>
  ),
};

const ConfirmChoiceModal = ({ option, onConfirm, onCancel, submitting }) => {
  if (!option) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-2">Confirm your choice</h3>
        <p className="text-sm text-gray-600 mb-6">
          You are about to select <span className="font-semibold">{option.label}</span>.
          {option.description && (
            <span className="block mt-2 text-gray-500">{option.description}</span>
          )}
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            className="flex-1 bg-[#F04438] text-white font-bold py-2.5 px-4 rounded-lg hover:bg-red-700 disabled:opacity-60 transition-colors"
          >
            {submitting ? 'Processing…' : 'Confirm'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="flex-1 bg-white text-gray-700 font-bold py-2.5 px-4 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-60 transition-colors"
          >
            Go back
          </button>
        </div>
      </div>
    </div>
  );
};

const PjoRejectionChoicePanel = ({
  bookingToken,
  onChoiceComplete,
  onChangeFlight,
}) => {
  const dispatch = useDispatch();
  const { getFormattedWithBoth } = useCurrencyDisplay();
  const {
    options,
    loading,
    submitting,
    expired,
    error,
    loadOptions,
    submitChoice,
  } = usePjoRejection();

  const [pendingConfirm, setPendingConfirm] = useState(null);
  const countdown = useCountdown(options?.choice_deadline);

  useEffect(() => {
    if (bookingToken) {
      loadOptions(bookingToken);
    }
  }, [bookingToken, loadOptions]);

  const handleSelectOption = async (option) => {
    if (submitting || countdown.expired) return;

    if (option.id === 'change_flight') {
      const result = await submitChoice(bookingToken, option.id);
      if (!result.success) {
        toast.error(result.message);
        if (result.expired && onChoiceComplete) {
          onChoiceComplete({ expired: true });
        }
        return;
      }

      toast.success('You can now search for a replacement flight — no change fee applies.');
      const rejectedLegTokens =
        result.data?.rejected_leg_tokens ||
        options?.rejected_legs?.map((leg) => leg.booking_token).filter(Boolean) ||
        [];

      try {
        await dispatch(getUserProfile()).unwrap();
      } catch {
        // Non-blocking — search page can still use URL booking_token
      }

      if (onChangeFlight) {
        onChangeFlight({
          referenceNumber: result.data?.change_flight || options?.reference_number,
          rejectedLegTokens,
        });
      }
      return;
    }

    setPendingConfirm(option);
  };

  const handleConfirmChoice = async () => {
    if (!pendingConfirm) return;

    const result = await submitChoice(bookingToken, pendingConfirm.id);
    setPendingConfirm(null);

    if (!result.success) {
      toast.error(result.message);
      if (result.expired && onChoiceComplete) {
        onChoiceComplete({ expired: true });
      }
      return;
    }

    if (pendingConfirm.id === 'full_refund') {
      toast.success('Full refund initiated. Your booking has been cancelled.');
    } else if (pendingConfirm.id === 'rejected_leg_refund') {
      toast.success('Refund initiated for the declined flight. Your other flights remain booked.');
    } else {
      toast.success(result.message || 'Your choice has been recorded.');
    }

    if (onChoiceComplete) {
      onChoiceComplete({ choice: pendingConfirm.id });
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-6 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-amber-400 border-t-amber-700 rounded-full animate-spin" />
          <p className="text-sm font-medium text-amber-900">Loading your options…</p>
        </div>
      </div>
    );
  }

  if (expired || (countdown.expired && options)) {
    return (
      <div className="rounded-xl border-2 border-gray-300 bg-gray-50 p-6 mb-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">Decision deadline passed</h3>
            <p className="text-sm text-gray-600 mt-1">
              {error ||
                'The time to choose an option has expired. Your booking may have been automatically refunded. Please refresh your booking list.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !options) {
    return (
      <div className="rounded-xl border-2 border-red-200 bg-red-50 p-6 mb-6">
        <p className="text-sm font-medium text-red-800">{error}</p>
        <button
          type="button"
          onClick={() => loadOptions(bookingToken)}
          className="mt-3 text-sm font-bold text-red-700 underline hover:no-underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!options?.options?.length) return null;

  const primaryReason =
    options.rejected_legs?.[0]?.rejection_reason || 'No reason provided by the airliner.';

  return (
    <>
      <div className="rounded-xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 p-6 mb-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-5">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-full bg-amber-200 flex items-center justify-center shrink-0">
              <svg className="w-6 h-6 text-amber-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-amber-950">
                Your flight was declined by the airliner
              </h3>
              <p className="text-sm text-amber-900/80 mt-1">
                Please choose an option below. No refund will be processed until you decide.
              </p>
              <p className="text-sm text-amber-950 mt-2">
                <span className="font-semibold">Reason:</span> {primaryReason}
              </p>
            </div>
          </div>

          {options.choice_deadline && (
            <div
              className={`shrink-0 rounded-lg px-4 py-3 text-center ${
                countdown.urgent ? 'bg-red-100 border border-red-200' : 'bg-white border border-amber-200'
              }`}
            >
              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                Decide before
              </div>
              <div
                className={`text-xl font-bold tabular-nums ${
                  countdown.urgent ? 'text-red-700' : 'text-amber-900'
                }`}
              >
                {countdown.label}
              </div>
              <div className="text-[10px] text-gray-500 mt-0.5">
                {formatDateTime(options.choice_deadline)}
              </div>
            </div>
          )}
        </div>

        {options.rejected_legs?.length > 0 && (
          <div className="mb-5 rounded-lg bg-white/70 border border-amber-200/80 p-4">
            <p className="text-xs font-bold uppercase tracking-wide text-amber-900 mb-2">
              Declined flight{options.rejected_legs.length > 1 ? 's' : ''}
            </p>
            <div className="space-y-2">
              {options.rejected_legs.map((leg) => (
                <div
                  key={leg.booking_token}
                  className="flex flex-wrap items-center justify-between gap-2 text-sm"
                >
                  <span className="font-semibold text-gray-900">
                    {leg.flight_number} · {formatDateTime(leg.departure_date_time)}
                  </span>
                  {leg.paid_amount != null && (
                    <span className="text-gray-600">
                      Paid: {getFormattedWithBoth(parseFloat(leg.paid_amount))}
                    </span>
                  )}
                </div>
              ))}
            </div>
            {options.confirmed_legs?.length > 0 && (
              <>
                <p className="text-xs font-bold uppercase tracking-wide text-green-800 mt-4 mb-2">
                  Confirmed flights (unchanged)
                </p>
                <div className="space-y-1">
                  {options.confirmed_legs.map((leg) => (
                    <div key={leg.booking_token} className="text-sm text-gray-700">
                      {leg.flight_number} · {formatDateTime(leg.departure_date_time)}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {options.options.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => handleSelectOption(option)}
              disabled={submitting || countdown.expired}
              className="group text-left rounded-xl border-2 border-amber-200 bg-white p-4 hover:border-jet-yellow hover:shadow-md transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-800 flex items-center justify-center shrink-0 group-hover:bg-jet-yellow group-hover:text-gray-900 transition-colors">
                  {OPTION_ICONS[option.id] || OPTION_ICONS.full_refund}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-gray-900 text-sm leading-snug">{option.label}</div>
                  {option.description && (
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">{option.description}</p>
                  )}
                  {option.refund_amount != null && (
                    <p className="text-xs font-bold text-green-700 mt-2">
                      Refund: {getFormattedWithBoth(parseFloat(option.refund_amount))}
                      <span className="font-normal text-gray-500"> · No cancellation fee</span>
                    </p>
                  )}
                  {option.id === 'change_flight' && (
                    <p className="text-xs font-bold text-green-700 mt-2">No change fee</p>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <ConfirmChoiceModal
        option={pendingConfirm}
        onConfirm={handleConfirmChoice}
        onCancel={() => setPendingConfirm(null)}
        submitting={submitting}
      />
    </>
  );
};

export default PjoRejectionChoicePanel;
